import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCreateVolumeAndCost,
  useDeleteVolumeAndCost,
  useGetVolumeAndCost,
  useUpdateVolumeAndCost
} from "../../../../../api/volumeAndCost";
import { Button } from "../../../../../components/ui/common/button";
import { Card, CardContent } from "../../../../../components/ui/common/card";
import { Form } from "../../../../../components/ui/common/form";
import { Input } from "../../../../../components/ui/common/input";
import { Skeleton } from "../../../../../components/ui/common/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import ConfirmModal from "../../../../../components/ui/widget/ConfirmModal/ConfirmModal";
import { successMessage, warningMessage } from "../../../../../helpers/ErrorHandler";
import { convertToKg, valueGraterThanZero } from "../../../../../utils/utils";
import { IngredientRowViewModel } from "../../../ingredientList/types/ingredientListView.types";
import {
  INGREDIENT_DETAILS_TABLE_CONTAINER_CLASS,
  INGREDIENT_DETAILS_TABLE_ROW_CLASS
} from "../shared/tableStyles";

interface VolumeAndCostTabProps {
  ingredientDetails: IngredientRowViewModel | undefined;
}

interface VolumeAndCostFormValues {
  year: number;
  volume: number;
  cost: number;
  volumeType: string;
}

interface InlineEditValues {
  volume: number | string;
  cost: number | string;
}

const StatCard = ({ label, value, unit }: { label: string; value: string; unit: string }) => (
  <Card className="tw-rounded-lg tw-border-border tw-bg-white tw-py-4">
    <CardContent className="tw-px-4">
      <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
        {label}
      </p>
      <p className="tw-mt-1 tw-font-mono tw-text-2xl tw-font-bold tw-text-foreground">{value}</p>
      <p className="tw-text-xs tw-text-muted-foreground">{unit}</p>
    </CardContent>
  </Card>
);

/**
 * "Volume And Cost" tab — Tailwind rebuild of the shared legacy
 * `VolumeAndCost` component (src/components/volumeAndCost/VolumeAndCost.tsx),
 * same create/update/delete-by-year behavior, off Semantic UI (`Grid`/
 * `Table`/`DropDown`/`InputNumber`) and onto the Tailwind equivalents.
 *
 * Existing rows are edited inline in the table (pencil → editable cells →
 * checkmark/X to save/cancel); the top "Add production year" form is only
 * for adding a brand new year.
 */
const VolumeAndCostTab = ({ ingredientDetails }: VolumeAndCostTabProps) => {
  const ingredientId = ingredientDetails?.ingredient_id as string;
  const supplierId = ingredientDetails?.supplier_id as string;
  const details = ingredientDetails?.supplierIngredientDetails as
    | { unit_size?: number; unit_type?: string }
    | null
    | undefined;
  const unitSize = Number(details?.unit_size) || 1;
  const unitType = details?.unit_type || "kg";

  const { data: volumeAndCost, isLoading, refetch } = useGetVolumeAndCost(supplierId, ingredientId);
  const { mutate: createVolumeAndCost } = useCreateVolumeAndCost();
  const { mutate: updateVolumeAndCost } = useUpdateVolumeAndCost();
  const { mutate: deleteVolumeAndCost } = useDeleteVolumeAndCost();

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => [currentYear, currentYear - 1, currentYear - 2], [currentYear]);

  const [volumeType, setVolumeType] = useState("L");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [inlineEditId, setInlineEditId] = useState<string | null>(null);
  const [inlineValues, setInlineValues] = useState<InlineEditValues>({ volume: 0, cost: 0 });

  const defaultValues: VolumeAndCostFormValues = {
    year: currentYear,
    volume: 0,
    cost: 0,
    volumeType: "L"
  };

  const methods = useForm<VolumeAndCostFormValues>({ defaultValues });
  const { control, handleSubmit, reset, setValue, formState } = methods;

  const list = (volumeAndCost as Array<Record<string, any>> | undefined) || [];

  const stats = useMemo(() => {
    const yearsRecorded = list.length;
    const totalUnits = list.reduce((sum, item) => sum + (Number(item?.volume) || 0), 0);
    const totalVolume = totalUnits * convertToKg(unitSize, unitType);
    const averagePrice = yearsRecorded
      ? list.reduce((sum, item) => sum + (Number(item?.cost) || 0), 0) / yearsRecorded
      : 0;
    return { yearsRecorded, totalUnits, totalVolume, averagePrice };
  }, [list, unitSize, unitType]);

  const resetState = () => {
    setVolumeType("L");
    reset(defaultValues);
  };

  const checkYearValidation = (data: VolumeAndCostFormValues) => {
    if (list.some(item => item?.year === data.year)) {
      warningMessage("This year has already recode in cost and volume");
      return false;
    }
    return true;
  };

  const onSelectYear = (value: string) => {
    setValue("year", Number(value));
  };

  const onSubmit = (data: VolumeAndCostFormValues) => {
    if (!checkYearValidation(data)) return;
    const payload = {
      ...data,
      volumeType,
      ownerId: supplierId,
      sourceId: ingredientId,
      // API rejects the request without these — legacy VolumeAndCost.tsx always
      // sends fromDate/toDate as null since this tab has no date-range inputs.
      fromDate: null,
      toDate: null
    };

    createVolumeAndCost(payload, {
      onSuccess: () => {
        successMessage("Cost and volume recode are created");
        refetch();
        resetState();
      }
    });
  };

  const onStartInlineEdit = (data: Record<string, any>) => {
    setInlineEditId(data._id);
    setInlineValues({ volume: data?.volume ?? 0, cost: data?.cost ?? 0 });
  };

  const onCancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineValues({ volume: 0, cost: 0 });
  };

  const onSaveInlineEdit = (data: Record<string, any>) => {
    if (!valueGraterThanZero(inlineValues.volume)) {
      warningMessage("Number of units must be greater than 0");
      return;
    }
    if (!valueGraterThanZero(inlineValues.cost)) {
      warningMessage("Price must be greater than 0");
      return;
    }

    updateVolumeAndCost(
      {
        ...data,
        volume: Number(inlineValues.volume),
        cost: Number(inlineValues.cost),
        ownerId: supplierId,
        sourceId: ingredientId,
        fromDate: null,
        toDate: null,
        _id: data._id
      },
      {
        onSuccess: () => {
          refetch();
          onCancelInlineEdit();
        }
      }
    );
  };

  if (isLoading) {
    return <Skeleton className="tw-h-48 tw-w-full" />;
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card className="tw-rounded-lg tw-border-border tw-bg-white tw-py-4">
        <CardContent className="tw-px-4">
          <p className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
            Add Production Year
          </p>
          <Form {...methods}>
            <div className="tw-grid tw-grid-cols-2 tw-items-start tw-gap-3 md:tw-grid-cols-[1fr_1fr_1fr_auto]">
              <FormSelect
                control={control}
                name="year"
                label="Year"
                placeholder="Year"
                options={yearOptions.map(year => ({ value: year, label: String(year) }))}
                onChange={onSelectYear}
              />
              <FormInputNumber
                control={control}
                name="volume"
                label="Number of units"
                placeholder="Number of units"
                required
                rules={{
                  validate: (value: unknown) =>
                    valueGraterThanZero(value) || "Number of units must be greater than 0"
                }}
                errorMessage={formState.errors.volume?.message as string}
              />
              <FormInputNumber
                control={control}
                name="cost"
                label="Price per unit"
                placeholder="Price per unit"
                required
                rules={{
                  validate: (value: unknown) =>
                    valueGraterThanZero(value) || "Price must be greater than 0"
                }}
                errorMessage={formState.errors.cost?.message as string}
              />
              <div className="tw-flex tw-flex-col tw-gap-4">
                <span
                  aria-hidden="true"
                  className="tw-invisible tw-text-sm tw-font-medium tw-leading-none"
                >
                  &nbsp;
                </span>
                <Button
                  type="button"
                  className="tw-h-10 tw-w-fit tw-gap-1.5"
                  onClick={handleSubmit(onSubmit)}
                >
                  <Plus className="tw-h-4 tw-w-4" />
                  Add year
                </Button>
              </div>
            </div>
          </Form>
        </CardContent>
      </Card>

      <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
        <StatCard label="Years recorded" value={String(stats.yearsRecorded)} unit="entries" />
        <StatCard label="Total units" value={stats.totalUnits.toLocaleString()} unit="units" />
        <StatCard label="Total volume" value={stats.totalVolume.toLocaleString()} unit="kg" />
        <StatCard label="Average price" value={stats.averagePrice.toFixed(2)} unit="per unit" />
      </div>

      <div className={INGREDIENT_DETAILS_TABLE_CONTAINER_CLASS}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Year</TableHead>
              <TableHead className="tw-text-right">Number of units</TableHead>
              <TableHead className="tw-text-right">Volume (kg)</TableHead>
              <TableHead className="tw-text-right">Price per unit</TableHead>
              <TableHead className="tw-text-right">Revenue</TableHead>
              <TableHead className="tw-text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((data, index) => {
              const isEditingRow = inlineEditId === data._id;
              const numberOfUnits = isEditingRow
                ? Number(inlineValues.volume) || 0
                : Number(data?.volume) || 0;
              const price = isEditingRow ? Number(inlineValues.cost) || 0 : Number(data?.cost) || 0;
              const calculatedVolume = numberOfUnits * convertToKg(unitSize, unitType);
              const revenue = numberOfUnits * price;
              return (
                <TableRow key={data._id || index} className={INGREDIENT_DETAILS_TABLE_ROW_CLASS}>
                  <TableCell>{data?.year ?? ""}</TableCell>
                  <TableCell className="tw-text-right tw-font-mono">
                    {isEditingRow ? (
                      <Input
                        type="number"
                        className="tw-h-8 tw-text-right tw-font-mono"
                        value={inlineValues.volume}
                        onChange={e =>
                          setInlineValues(prev => ({ ...prev, volume: e.target.value }))
                        }
                      />
                    ) : (
                      numberOfUnits ? numberOfUnits.toLocaleString() : ""
                    )}
                  </TableCell>
                  <TableCell className="tw-text-right tw-font-mono">
                    {calculatedVolume ? calculatedVolume.toLocaleString() : ""}
                  </TableCell>
                  <TableCell className="tw-text-right tw-font-mono">
                    {isEditingRow ? (
                      <Input
                        type="number"
                        className="tw-h-8 tw-text-right tw-font-mono"
                        value={inlineValues.cost}
                        onChange={e => setInlineValues(prev => ({ ...prev, cost: e.target.value }))}
                      />
                    ) : (
                      price || ""
                    )}
                  </TableCell>
                  <TableCell className="tw-text-right tw-font-mono tw-text-muted-foreground">
                    {revenue ? revenue.toLocaleString() : ""}
                  </TableCell>
                  <TableCell className="tw-text-right">
                    <div className="tw-flex tw-justify-end tw-gap-3">
                      {isEditingRow ? (
                        <>
                          <button type="button" onClick={() => onSaveInlineEdit(data)}>
                            <Check className="tw-h-4 tw-w-4 tw-text-primary" />
                          </button>
                          <button type="button" onClick={onCancelInlineEdit}>
                            <X className="tw-h-4 tw-w-4 tw-text-destructive" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={() => onStartInlineEdit(data)}>
                            <Pencil className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteId(data._id);
                              setVisibleDeleteModal(true);
                            }}
                          >
                            <Trash2 className="tw-h-4 tw-w-4 tw-text-destructive" />
                          </button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <ConfirmModal
        isOpen={visibleDeleteModal}
        onClose={() => {
          setVisibleDeleteModal(false);
          setDeleteId(null);
        }}
        onConfirm={() => {
          if (!deleteId) return;
          deleteVolumeAndCost(deleteId, {
            onSuccess: () => {
              refetch();
              successMessage("Volume and cost delete successfully");
            }
          });
          setVisibleDeleteModal(false);
          setDeleteId(null);
        }}
        title="Delete Cost and Volume Data"
        message="Are you sure you want to delete cost and volume data?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </div>
  );
};

export default VolumeAndCostTab;
